<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import CompaniesRepository from '@repositories/companies.repository';
import { ICountry } from '../../types/countries.type';
import CountriesRepository from '@repositories/countries.repository';
import { ICompany } from '../../types/company.type';
import DataTable from '@components/DataTable.vue';

const isLoadingCompanies = ref<boolean>(false);

const store = Store();
const { $toast } = useToast();
const { companies: companiesPinia }: any = storeToRefs(store);
const companies = ref<ICompany[]>([]);
const countries = ref<ICountry[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedCompanies = ref<ICompany | null>(null);
const isViewActive = ref<boolean>(true);
const searchFormRef = ref<any>(null);
const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'name',
    label: 'Nombre',
  },
  {
    key: 'restore',
    label: 'Restaurar',
  },
]);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'name',
    label: 'Nombre',
  },
  {
    key: 'country',
    label: 'Pais',
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'urlDomain',
    label: 'URL de dominio',
  },
  {
    key: 'requestType',
    label: 'Envia datos por',
  },
  {
    key: 'email',
    label: 'Email 1',
    formatter: (e: any) => e?.email_1 || null,
  },
  {
    key: 'email',
    label: 'Email 2',
    formatter: (e: any) => e?.email_2 || null,
  },
  {
    key: 'email',
    label: 'Email 3',
    formatter: (e: any) => e?.email_3 || null,
  },
  {
    key: 'email',
    label: 'Email 4',
    formatter: (e: any) => e?.email_4 || null,
  },
  {
    key: 'companyInfo',
    label: 'Editar',
  },
  {
    key: 'delete',
    label: 'Eliminar',
  },
]);
const fieldsFilter: any = ref([
  {
    key: 'search',
    title: 'Buscar',
    type: 'text',
    placeholder: 'Buscar por descripción del sponsor',
    value: null,
    cols: 3,
  },
]);

// API
const getCompaniesFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingCompanies.value = true;
  try {
    const { data } = await CompaniesRepository.getCompaniesPro({
      page,
      deleted,
      ...params,
    });
    if (data && Array.isArray(data?.companies)) {
      paginationData.value = data.pagination;
      updateCompanies(data.companies);
      return data.companies;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching companies:', error);
    return [];
  } finally {
    isLoadingCompanies.value = false;
  }
};

const getCountriesFromApi = async () => {
  const data = await CountriesRepository.getAllcountries();
  countries.value = data.data;
};

const updateCompanies = (newCompanies: ICompany[]) => {
  companies.value = newCompanies;
  store.setProp({ key: 'companies', value: newCompanies });
};

const loadCompanies = async () => {
  await getCompaniesFromAPI(1);
};

onMounted(() => {
  loadCompanies();
  getCountriesFromApi();
});

// Create inputs from modals
const fieldsInputCompanies = computed(() => {
  return [
    {
      key: 'name',
      title: 'Nombre del sponsor',
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: 'Nombre del sponsor',
      value: selectedCompanies.value?.name,
      cols: 12,
    },
    {
      key: 'countryId',
      label: 'description',
      selectLabel: null,
      value: selectedCompanies.value?.countryId,
      searchable: true,
      title: 'Pais',
      trackBy: 'id',
      type: 'select',
      placeholder: 'Nombre del pais',
      formatValue: (val: any) => val.id,
      cols: 12,
      options: countries,
    },
    {
      key: 'urlDomain',
      title: 'URL de dominio',
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: 'URL de dominio',
      value: selectedCompanies.value?.urlDomain,
      cols: 12,
    },
    {
      key: 'requestType',
      label: 'description',
      value: selectedCompanies.value?.requestType,
      searchable: true,
      title: 'Envia datos por',
      type: 'select',
      trackBy: 'description',
      placeholder: 'Tipo de riesgo',
      formatValue: (val: any) => val.description,
      cols: 12,
      options: [
        { id: 1, description: 'WEB' },
        { id: 2, description: 'API' },
      ],
    },
    {
      key: 'email_1',
      title: 'Email 1',
      type: 'email',
      placeholder: 'Email 1',
      value: selectedCompanies.value?.email?.email_1,
      cols: 12,
    },
    {
      key: 'email_2',
      title: 'Email 2',
      type: 'email',
      placeholder: 'Email 2',
      value: selectedCompanies.value?.email?.email_2,
      cols: 12,
    },
    {
      key: 'email_3',
      title: 'Email 3',
      type: 'email',
      placeholder: 'Email 3',
      value: selectedCompanies.value?.email?.email_3,
      cols: 12,
    },
    {
      key: 'email_4',
      title: 'Email 4',
      type: 'email',
      placeholder: 'Email 4',
      value: selectedCompanies.value?.email?.email_4,
      cols: 12,
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getCompaniesFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = 'Agregar sponsor';
  isModalOpen.value = true;
  modalType.value = 'modalCreateCompanies';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedCompanies.value = null;
};

const handleActive = async () => {
  const companies = await getCompaniesFromAPI(1);
  updateCompanies(companies);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const companiesDeleted = await getCompaniesFromAPI(1, true);
  updateCompanies(companiesDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ICompany) => {
  modalTitle.value = isViewActive.value
    ? `Borrar el sponsor con id: ${item.id}`
    : `Restaurar el sponsor con id: ${item.id}`;
  modalType.value = 'modalDialog';
  selectedCompanies.value = item;
  isModalOpen.value = true;
};

// Filter Functions

const handleOnSubmit = async ({ items }: any) => {
  getCompaniesFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getCompaniesFromAPI(1, false);
};

//CRUD
const handleCreateCompanies = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const data = await CompaniesRepository.createCompany(items);
    store.setProp({ key: 'companies', value: [...companies.value, data] });
    companies.value = [...companies.value, data];
    $toast?.success(`Sponsor agregado con éxito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar crear');
  }

  handleClose();
};

const handleSelectCompanies = (CompaniesData: ICompany) => {
  modalTitle.value = 'Actualizar sponsor';
  (isModalOpen.value = true), (modalType.value = 'modalUpdateCompanies');
  selectedCompanies.value = CompaniesData;
};

const handleUpdateCompanies = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedCompanies.value) return;
  const assetForUpdate = {
    ...items,
    id: selectedCompanies.value.id,
    emailsId: selectedCompanies.value?.emailsId,
  };

  try {
    const data = await CompaniesRepository.updateCompany(assetForUpdate);
    let index = store.companies.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'companies', index, value: data });
    }
    $toast?.success(`Sponsor actualizado con éxito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar actualizar');
  }

  handleClose();
};

const handleDeleteRestoreCompanies = async () => {
  if (selectedCompanies?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await CompaniesRepository.deleteCompany(
          selectedCompanies.value.id,
        );
      } else {
        response = await CompaniesRepository.restoreCompany(
          selectedCompanies.value.id,
        );
      }

      if (response === 200) {
        store.deleteItem({
          key: 'companies',
          value: selectedCompanies?.value?.id,
        });
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error('Error al intentar eliminar');
      }
      $toast?.error('Error al intentar restaurar');
    }

    handleClose();
  }
};
</script>

<template>
  <Layout title="Sponsors" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>Activos</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>Inactivos</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content> Agregar sponsor </template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content> Buscar </template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content> Limpiar filtro </template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingCompanies"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="companiesPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(companyInfo)="assetData">
          <div
            @click="handleSelectCompanies(assetData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateCompanies'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputCompanies"
            @onSubmit="handleCreateCompanies"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Agregar sponsor</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateCompanies'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputCompanies"
            @onSubmit="handleUpdateCompanies"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Actualizar</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `¿Está seguro que desea borrar el sponsor ${selectedCompanies?.name}?`
                : `¿Está seguro que desea restaurar el sponsor ${selectedCompanies?.name}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreCompanies"
            >
              <template #content>
                <div>
                  <span>Confirmar</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
